import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import {
  Menu, X, Phone, Mail, ArrowRight, MapPin, Calendar, Clock, Globe,
  GraduationCap, Scale, DollarSign, HeartPulse, BookOpen, Leaf,
  Sparkles, Music2, Stethoscope, Users, ChevronLeft, ChevronRight, Camera,
  Gift, Mic2, Presentation, Ticket, Sun, Languages, Download, FileText, CheckCircle2
} from "lucide-react";
import { WORKSHOPS, CATEGORIES, AUDIENCES, LANGUAGES, FORMATS } from "@/data/workshops";
import { GALLERY_PHOTOS } from "@/data/gallery";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_latino-site-revamp/artifacts/9lxlfmgs_Lat.Ins.Logo.jpg";
const HERO_URL = "https://customer-assets.emergentagent.com/job_latino-site-revamp/artifacts/cuuu6gsa_Somerville.Photo.jpg";

const CATEGORY_ICON = {
  "Parental Workshop": Users,
  "Legal Workshop": Scale,
  "Financial Literacy": DollarSign,
  "Wellness Workshop": HeartPulse,
  "Educational Workshop": BookOpen,
  "Environmental Workshop": Leaf,
  "Personal Development": Sparkles,
  "Cultural Workshop": Music2,
  "Health & Wellness": Stethoscope,
};

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Workshops", href: "#workshops" },
  { label: "Conferences", href: "#conferences" },
  { label: "Handbook", href: "#handbook" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

const HANDBOOK_URL = "https://customer-assets.emergentagent.com/job_latino-site-revamp/artifacts/nwhwtozc_Latino_Institute_Handbook_Full_Editable.pdf";

function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="site-header">
      {/* Top utility bar */}
      <div className="hidden md:block bg-[#1A1A1A] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between text-xs py-2">
          <div className="flex items-center gap-2 opacity-90">
            <MapPin size={14} className="text-[#E69F1B]" />
            <span>Serving New Jersey families since 2004</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="tel:+12014010291" className="flex items-center gap-2 hover:text-[#E69F1B] transition" data-testid="top-bar-phone">
              <Phone size={14} /> 201-401-0291
            </a>
            <a href="mailto:parentconferences1@gmail.com" className="flex items-center gap-2 hover:text-[#E69F1B] transition" data-testid="top-bar-email">
              <Mail size={14} /> parentconferences1@gmail.com
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-6 sm:px-12 flex items-center justify-between h-20">
        <a href="#home" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src={LOGO_URL} alt="The Latino Institute" className="h-12 w-auto" />
          <span className="hidden sm:block leading-tight">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[#8B3A36] font-semibold">Est. 2004 · New Jersey</span>
            <span className="block font-['Outfit'] text-lg font-bold text-[#1A1A1A]">The Latino Institute</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-0.5" data-testid="primary-nav">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href}
              className="px-3 py-2 text-sm font-medium text-[#1A1A1A] rounded-full hover:bg-[#F0ECE1] transition"
              data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href="#contact" className="btn-primary text-sm hidden sm:inline-flex" data-testid="header-cta">
            Schedule a Workshop <ArrowRight size={16} />
          </a>
          <button
            className="md:hidden p-2 rounded-full border border-[#E5E1D8]"
            onClick={() => setOpen(v => !v)}
            aria-label="Toggle navigation"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[#E5E1D8] bg-white" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-2">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href}
                 onClick={() => setOpen(false)}
                 className="px-3 py-3 rounded-xl hover:bg-[#F0ECE1] text-[#1A1A1A] font-medium"
                 data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}>
                {l.label}
              </a>
            ))}
            <a href="tel:+12014010291" className="px-3 py-3 text-sm flex items-center gap-2 text-[#5C5C5C]"><Phone size={14}/> 201-401-0291</a>
            <a href="mailto:parentconferences1@gmail.com" className="px-3 py-3 text-sm flex items-center gap-2 text-[#5C5C5C]"><Mail size={14}/> parentconferences1@gmail.com</a>
          </div>
        </div>
      )}
    </header>
  );
}

function HeroAbout() {
  return (
    <section id="home" className="relative overflow-hidden" data-testid="hero-section">
      {/* Full-width photo banner (whole photo, not cropped) */}
      <div className="relative w-full bg-[#1A1A1A]" data-testid="hero-photo-banner">
        <div className="sun-glow -top-20 left-1/4" />
        <div className="sun-glow -bottom-20 right-1/4" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10 flex items-center justify-center">
          <img
            src={HERO_URL}
            alt="The Latino Institute community at a parent workshop in Somerville, NJ"
            className="w-full h-auto max-h-[78vh] object-contain rounded-2xl"
            data-testid="hero-image"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pb-8 sm:pb-10 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFD894]">Parent Education Conference · Somerville, NJ</p>
          <span className="chip bg-[#E69F1B] text-[#1A1A1A]">Bilingual · Parents · Educators · Students</span>
        </div>
      </div>

      {/* About Us copy below the photo */}
      <div className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-7" id="about">
            <div className="reveal reveal-1">
              <span className="chip bg-[#F0ECE1] text-[#8B3A36]" data-testid="hero-overline">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8B3A36]" /> EST. 2004 · NEW JERSEY · ABOUT US
              </span>
            </div>
            <h1 className="reveal reveal-2 mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-[#1A1A1A]" data-testid="hero-title">
              Empowering Parents.
              <span className="block text-[#8B3A36]">Elevating Education.</span>
            </h1>
            <p className="reveal reveal-3 mt-7 text-lg leading-relaxed text-[#5C5C5C]" data-testid="hero-about-text">
              Since 2004, the Latino Institute has partnered with school districts, community
              organizations, and public education support groups throughout New Jersey to provide
              parents with essential educational information and resources. We offer dynamic and
              empowering parent conferences and workshops designed to strengthen family
              engagement and help parents become active, informed, and effective partners in
              their children&rsquo;s education.
            </p>
            <p className="reveal reveal-4 mt-4 text-base leading-relaxed text-[#5C5C5C]">
              Our mission is to provide parents with the most current tools, knowledge, and
              strategies to support both their own educational growth and the academic success
              of their children.
            </p>
            <div className="reveal reveal-5 mt-9 flex flex-wrap gap-4" data-testid="hero-cta-group">
              <a href="#workshops" className="btn-primary" data-testid="hero-explore-btn">
                Explore 26 Workshops <ArrowRight size={18} />
              </a>
              <a href="#contact" className="btn-outline" data-testid="hero-contact-btn">
                <Phone size={16}/> Book a Conference
              </a>
            </div>
          </div>

          <div className="md:col-span-5 md:pl-6 md:border-l md:border-[#E5E1D8]">
            <div className="grid grid-cols-1 gap-6" data-testid="hero-stats">
              <Stat value="20+" label="Years of impact in New Jersey" />
              <Stat value="26" label="Bilingual workshops available" />
              <Stat value="1000s" label="NJ families served" />
            </div>
            <div className="mt-8 bg-white rounded-2xl border border-[#E5E1D8] p-5 flex items-center gap-3" data-testid="trusted-partner-card">
              <div className="w-11 h-11 rounded-full bg-[#FFF4DE] flex items-center justify-center shrink-0">
                <GraduationCap className="text-[#E69F1B]" size={22} />
              </div>
              <div className="leading-tight">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8B3A36] font-semibold">Trusted Partner</p>
                <p className="text-sm text-[#1A1A1A] font-medium">NJ School Districts, the NJEA, local union FAST programs &amp; community organizations</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-['Outfit'] text-3xl font-extrabold text-[#1A1A1A]">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C] mt-1">{label}</div>
    </div>
  );
}

function Workshops() {
  const [audience, setAudience] = useState("All Audiences");
  const [category, setCategory] = useState("All Categories");
  const [language, setLanguage] = useState("All Languages");
  const [format, setFormat] = useState("All Formats");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return WORKSHOPS.filter(w => {
      if (audience !== "All Audiences" && w.audience !== audience) return false;
      if (category !== "All Categories" && w.category !== category) return false;
      if (language !== "All Languages" && w.language !== language) return false;
      if (format !== "All Formats" && w.format !== format) return false;
      if (query.trim() && !(`${w.title} ${w.description}`.toLowerCase().includes(query.toLowerCase()))) return false;
      return true;
    });
  }, [audience, category, language, format, query]);

  return (
    <section id="workshops" className="relative py-20 sm:py-28 bg-[#FDFBF7] border-t border-[#E5E1D8]" data-testid="workshops-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="max-w-2xl">
            <span className="chip bg-[#F0ECE1] text-[#8B3A36]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8B3A36]" /> OUR WORKSHOPS
            </span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-bold text-[#1A1A1A]" data-testid="workshops-title">
              Empowering Parents and Teachers
            </h2>
            <p className="mt-3 text-[#5C5C5C] text-lg">
              Available in English, Spanish, or Bilingual &middot; In-Person or Virtual
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <a href="mailto:parentconferences1@gmail.com" className="text-sm text-[#5C5C5C] hover:text-[#1A1A1A] flex items-center gap-2"><Mail size={14}/> parentconferences1@gmail.com</a>
            <a href="tel:+12014010291" className="text-sm text-[#5C5C5C] hover:text-[#1A1A1A] flex items-center gap-2"><Phone size={14}/> Bill Colon · 201-401-0291</a>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E5E1D8] rounded-3xl p-5 sm:p-6 mb-10" data-testid="workshop-filters">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search workshops..."
              className="ti-select pl-4"
              style={{ backgroundImage: 'none', paddingRight: '1rem' }}
              data-testid="workshop-search-input"
            />
            <FilterSelect label="Audience" value={audience} setValue={setAudience} options={AUDIENCES} testId="filter-audience" />
            <FilterSelect label="Category" value={category} setValue={setCategory} options={CATEGORIES} testId="filter-category" />
            <FilterSelect label="Language" value={language} setValue={setLanguage} options={LANGUAGES} testId="filter-language" />
            <FilterSelect label="Format" value={format} setValue={setFormat} options={FORMATS} testId="filter-format" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#8B3A36] font-semibold mt-5" data-testid="workshop-count">
            Showing {filtered.length} of {WORKSHOPS.length} workshops
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workshop-grid">
          {filtered.map((w, i) => {
            const Icon = CATEGORY_ICON[w.category] || BookOpen;
            return (
              <article
                key={w.title}
                className="ws-card bg-white border border-[#E5E1D8] rounded-3xl p-6 flex flex-col"
                data-testid={`workshop-card-${i}`}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="w-11 h-11 rounded-2xl bg-[#FFF4DE] flex items-center justify-center">
                    <Icon size={20} className="text-[#E69F1B]" />
                  </div>
                  <span className="chip bg-[#F0ECE1] text-[#1A1A1A]">{w.audience}</span>
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-[#1A1A1A] leading-snug" data-testid={`workshop-title-${i}`}>
                  {w.title}
                </h3>
                <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed line-clamp-5">{w.description}</p>
                <div className="mt-5 flex flex-wrap gap-2 text-[11px] text-[#5C5C5C]">
                  <span className="chip bg-[#FDFBF7] border border-[#E5E1D8]"><Globe size={12}/> {w.language}</span>
                  <span className="chip bg-[#FDFBF7] border border-[#E5E1D8]"><Calendar size={12}/> {w.format}</span>
                  <span className="chip bg-[#FDFBF7] border border-[#E5E1D8]"><Clock size={12}/> {w.duration}</span>
                </div>
                <div className="mt-6 pt-5 border-t border-[#E5E1D8] flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#8B3A36] font-semibold">{w.category}</span>
                  <a
                    href="#contact"
                    className="text-sm font-semibold text-[#1A1A1A] inline-flex items-center gap-1 hover:text-[#8B3A36] transition"
                    data-testid={`workshop-schedule-${i}`}
                  >
                    Schedule <ArrowRight size={14}/>
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-[#5C5C5C]" data-testid="workshop-empty-state">
            No workshops match your filters. Try widening your search.
          </div>
        )}
      </div>
    </section>
  );
}

function FilterSelect({ label, value, setValue, options, testId }) {
  return (
    <label className="block">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="ti-select w-full"
        data-testid={testId}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function Contact() {
  return (
    <section id="contact" className="relative bg-[#8B3A36] text-[#FDFBF7]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <span className="chip bg-white/10 text-[#FFD894]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E69F1B]" /> GET IN TOUCH
            </span>
            <h2 className="mt-4 text-4xl sm:text-6xl font-extrabold leading-[1.05]" data-testid="contact-title">
              Partner with the<br/>Latino Institute.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#FDFBF7]/80 max-w-xl">
              Whether you represent a school district, a community organization, or a
              parent group, we&rsquo;ll work with you to bring meaningful, bilingual workshops
              to your community.
            </p>
          </div>
          <div className="md:col-span-5 md:col-start-8 space-y-5">
            <ContactCard
              icon={<Phone size={18} className="text-[#1A1A1A]" />}
              label="Call Bill Colon"
              value="201-401-0291"
              href="tel:+12014010291"
              testId="contact-phone"
            />
            <ContactCard
              icon={<Mail size={18} className="text-[#1A1A1A]" />}
              label="Email Us"
              value="parentconferences1@gmail.com"
              href="mailto:parentconferences1@gmail.com"
              testId="contact-email"
            />
            <ContactCard
              icon={<MapPin size={18} className="text-[#1A1A1A]" />}
              label="Service Area"
              value={<>New Jersey · Statewide<span className="block text-sm font-normal text-[#5C5C5C] mt-0.5">National in Virtual Format</span></>}
              testId="contact-area"
            />
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-[#FDFBF7]/70">
          <div className="flex items-center gap-3" data-testid="footer-brand">
            <img src={LOGO_URL} alt="The Latino Institute" className="h-9 w-auto bg-white rounded-lg p-1" />
            <span>&copy; {new Date().getFullYear()} The Latino Institute. All rights reserved.</span>
          </div>
          <div className="flex gap-6">
            <a href="#about" className="hover:text-white transition" data-testid="footer-about">About</a>
            <a href="#workshops" className="hover:text-white transition" data-testid="footer-workshops">Workshops</a>
            <a href="#conferences" className="hover:text-white transition" data-testid="footer-conferences">Conferences</a>
            <a href="#handbook" className="hover:text-white transition" data-testid="footer-handbook">Handbook</a>
            <a href="#gallery" className="hover:text-white transition" data-testid="footer-gallery">Gallery</a>
            <a href="#contact" className="hover:text-white transition" data-testid="footer-contact">Contact</a>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReachCallout() {
  return (
    <section
      className="relative py-14 sm:py-20 bg-[#FDFBF7] border-t border-[#E5E1D8]"
      data-testid="reach-callout-section"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div
          className="relative bg-white border border-[#E5E1D8] rounded-3xl p-8 sm:p-12 shadow-[0_30px_80px_-40px_rgba(26,26,26,0.2)] overflow-hidden"
          data-testid="reach-callout-box"
        >
          {/* Decorative sun glow */}
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#E69F1B]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#8B3A36]/10 blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-2 flex md:justify-center">
              <div className="w-20 h-20 rounded-3xl bg-[#FFF4DE] border border-[#E5E1D8] flex items-center justify-center">
                <Globe size={36} className="text-[#E69F1B]" />
              </div>
            </div>

            <div className="md:col-span-10">
              <span className="chip bg-[#F0ECE1] text-[#8B3A36]" data-testid="reach-callout-overline">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8B3A36]" /> WHERE WE DELIVER
              </span>
              <p
                className="mt-4 font-['Outfit'] text-xl sm:text-2xl lg:text-3xl font-semibold leading-snug text-[#1A1A1A]"
                data-testid="reach-callout-text"
              >
                We offer <span className="text-[#8B3A36]">parent educational conferences</span> and
                <span className="text-[#8B3A36]"> individual workshops</span> in both
                <span className="text-[#1A1A1A] font-bold"> in-person</span> and
                <span className="text-[#1A1A1A] font-bold"> virtual</span> formats throughout New Jersey.
                Upon request, individual workshops may also be delivered virtually to audiences
                <span className="text-[#1A1A1A] font-bold"> nationwide</span>.
              </p>
              <div className="mt-6 flex flex-wrap gap-3" data-testid="reach-callout-pills">
                <span className="chip bg-[#F0ECE1] text-[#1A1A1A]">
                  <MapPin size={12} className="text-[#8B3A36]" /> NJ Statewide · In-Person &amp; Virtual
                </span>
                <span className="chip bg-[#F0ECE1] text-[#1A1A1A]">
                  <Globe size={12} className="text-[#8B3A36]" /> Nationwide · Virtual Workshops on Request
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ConferencePackage() {
  const items = [
    { icon: Gift, title: "Warm welcome & gift bags", text: "Every attendee is greeted personally and receives a curated gift bag at check-in." },
    { icon: Mic2, title: "Engaging keynote", text: "An inspiring keynote presentation that sets the tone and energizes the room." },
    { icon: Presentation, title: "Two 1-hour workshops", text: "Two concurrent or back-to-back sessions, with topics chosen by your district staff from our workshop menu." },
    { icon: Ticket, title: "Raffle of incentive gifts", text: "We close the conference with exciting prizes raffled off to attending families." },
  ];

  return (
    <section
      id="conferences"
      className="relative bg-[#1A1A1A] text-[#FDFBF7] overflow-hidden border-t border-[#2a2a2a]"
      data-testid="conference-section"
    >
      {/* Decorative sun glows */}
      <div className="sun-glow -top-20 -left-20 opacity-60" />
      <div className="sun-glow -bottom-32 -right-10 opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          {/* Left column: heading + description */}
          <div className="md:col-span-5">
            <span className="chip bg-white/10 text-[#FFD894]" data-testid="conference-overline">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E69F1B]" /> SIGNATURE OFFERING
            </span>
            <h2
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-white"
              data-testid="conference-title"
            >
              Parent Conference
              <span className="block text-[#E69F1B]">Package</span>
            </h2>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#FDFBF7]/80" data-testid="conference-description">
              The conferences are designed to <span className="text-white font-semibold">inform, inspire, and equip</span> parents
              with practical strategies to support their children&rsquo;s academic and personal success.
            </p>

            {/* Schedule + Language pills */}
            <div className="mt-8 flex flex-wrap gap-3" data-testid="conference-format-pills">
              <span className="chip bg-white/10 text-white border border-white/15">
                <Sun size={12} className="text-[#E69F1B]" /> Saturday mornings
              </span>
              <span className="chip bg-white/10 text-white border border-white/15">
                <Clock size={12} className="text-[#E69F1B]" /> Weekday evenings
              </span>
              <span className="chip bg-white/10 text-white border border-white/15">
                <Languages size={12} className="text-[#E69F1B]" /> English · Spanish · Bilingual
              </span>
            </div>

            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary" data-testid="conference-book-btn">
                Book a Conference <ArrowRight size={18} />
              </a>
              <a href="#workshops" className="btn-outline border-white text-white hover:bg-white hover:text-[#1A1A1A]" data-testid="conference-menu-btn">
                <BookOpen size={16}/> Browse Workshop Menu
              </a>
            </div>
          </div>

          {/* Right column: 4-up feature grid */}
          <div className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] text-[#FFD894] font-semibold mb-5" data-testid="conference-includes-label">
              Every conference includes
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="conference-features">
              {items.map(({ icon: Icon, title, text }, i) => (
                <div
                  key={title}
                  className="relative bg-white/[0.04] border border-white/10 rounded-3xl p-6 hover:bg-white/[0.08] hover:border-[#E69F1B]/60 transition group"
                  data-testid={`conference-feature-${i}`}
                >
                  <div className="absolute top-5 right-5 text-[#FFD894]/40 text-xs font-mono tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-[#E69F1B] flex items-center justify-center mb-5 group-hover:rotate-[-4deg] transition">
                    <Icon size={22} className="text-[#1A1A1A]" />
                  </div>
                  <h3 className="font-['Outfit'] text-lg font-semibold text-white leading-snug">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#FDFBF7]/70">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Handbook() {
  const chapters = [
    "Why parents do not attend family engagement events",
    "10 strategies for overcoming attendance barriers",
    "Building partnerships with community organizations",
    "Communications & marketing toolkit",
    "Parent Ambassador Program",
    "Event evaluation & continuous improvement",
    "Family engagement event planning checklist",
  ];

  return (
    <section
      id="handbook"
      className="relative py-20 sm:py-28 bg-[#FDFBF7] border-t border-[#E5E1D8] overflow-hidden"
      data-testid="handbook-section"
    >
      {/* Decorative glows */}
      <div className="absolute -top-20 left-1/3 w-96 h-96 rounded-full bg-[#E69F1B]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Left: Book cover mockup */}
          <div className="md:col-span-5 flex justify-center md:justify-start" data-testid="handbook-cover-wrap">
            <div className="relative">
              {/* Shadow / second page peek */}
              <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-2xl bg-[#8B3A36]/15 -z-10" />
              <div className="absolute inset-0 translate-x-6 translate-y-6 rounded-2xl bg-[#E69F1B]/15 -z-20" />

              <div
                className="relative w-[280px] sm:w-[320px] aspect-[3/4] rounded-2xl bg-white border border-[#E5E1D8] shadow-[0_30px_60px_-20px_rgba(26,26,26,0.35)] overflow-hidden flex flex-col"
                data-testid="handbook-cover"
              >
                {/* Top yellow band */}
                <div className="h-2 bg-gradient-to-r from-[#E69F1B] via-[#FFD894] to-[#E69F1B]" />

                {/* Cover content */}
                <div className="flex-1 flex flex-col p-7">
                  <div className="flex items-center gap-2">
                    <img src={LOGO_URL} alt="" className="h-8 w-auto" />
                    <span className="text-[9px] uppercase tracking-[0.22em] text-[#8B3A36] font-semibold leading-tight">
                      The Latino<br/>Institute
                    </span>
                  </div>

                  <div className="mt-auto">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#8B3A36] font-bold">For School District Personnel</span>
                    <h3 className="mt-3 font-['Outfit'] text-2xl sm:text-[26px] font-extrabold leading-[1.1] text-[#1A1A1A]">
                      Parent Outreach &amp; Family Engagement Handbook
                    </h3>
                    <p className="mt-3 text-xs text-[#5C5C5C] leading-snug">
                      Strategies for increasing parent participation in family engagement events, workshops, and conferences.
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-[#E5E1D8] flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[#5C5C5C]">
                    <span>Edition 2026</span>
                    <span className="flex items-center gap-1 text-[#E69F1B] font-bold"><FileText size={11}/> 24 pp</span>
                  </div>
                </div>
              </div>

              {/* Floating "FREE" badge */}
              <div className="absolute -top-4 -right-4 sm:-top-5 sm:-right-5 rotate-[8deg] bg-[#E69F1B] text-[#1A1A1A] rounded-full w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center text-center shadow-lg" data-testid="handbook-free-badge">
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold">100%</div>
                  <div className="font-['Outfit'] font-extrabold text-xl sm:text-2xl">FREE</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Description + CTA */}
          <div className="md:col-span-7">
            <span className="chip bg-[#F0ECE1] text-[#8B3A36]" data-testid="handbook-overline">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8B3A36]" /> FREE GUIDE FOR SCHOOL DISTRICTS
            </span>
            <h2
              className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-[#1A1A1A]"
              data-testid="handbook-title"
            >
              Parent Outreach &amp;
              <span className="block text-[#8B3A36]">Family Engagement Handbook</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#5C5C5C] max-w-2xl" data-testid="handbook-description">
              A practical guide written for school administrators, parent liaisons, and Title I coordinators.
              Learn why parents miss family engagement events &mdash; and discover proven strategies to
              build packed-room participation in workshops and parent education conferences.
            </p>

            <div className="mt-7" data-testid="handbook-chapters">
              <p className="text-xs uppercase tracking-[0.22em] text-[#8B3A36] font-semibold mb-3">What&rsquo;s inside</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {chapters.map((c, i) => (
                  <li key={c} className="flex items-start gap-2 text-sm text-[#1A1A1A]" data-testid={`handbook-chapter-${i}`}>
                    <CheckCircle2 size={16} className="text-[#E69F1B] mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href={HANDBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                download="Latino-Institute-Parent-Outreach-Handbook.pdf"
                className="btn-primary"
                data-testid="handbook-download-btn"
              >
                <Download size={18} /> Download the Free Handbook
              </a>
              <span className="text-sm text-[#5C5C5C] flex items-center gap-2">
                <FileText size={14} className="text-[#8B3A36]"/> PDF · 24 pages · No sign-up required
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const [openIdx, setOpenIdx] = useState(null);

  const close = () => setOpenIdx(null);
  const prev = (e) => { e?.stopPropagation(); setOpenIdx((i) => (i - 1 + GALLERY_PHOTOS.length) % GALLERY_PHOTOS.length); };
  const next = (e) => { e?.stopPropagation(); setOpenIdx((i) => (i + 1) % GALLERY_PHOTOS.length); };

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openIdx]);

  return (
    <section id="gallery" className="relative py-20 sm:py-28 bg-[#F0ECE1] border-t border-[#E5E1D8]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="chip bg-white text-[#8B3A36]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8B3A36]" /> COMMUNITY MOMENTS
            </span>
            <h2 className="mt-4 text-3xl sm:text-5xl font-bold text-[#1A1A1A]" data-testid="gallery-title">
              Our Work, In Pictures
            </h2>
            <p className="mt-3 text-[#5C5C5C] text-lg">
              Two decades of parent conferences, cultural celebrations, and workshops across New Jersey.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#5C5C5C]" data-testid="gallery-count">
            <Camera size={16}/> {GALLERY_PHOTOS.length} photos · click any to enlarge
          </div>
        </div>

        {/* CSS column masonry */}
        <div
          className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]"
          data-testid="gallery-grid"
        >
          {GALLERY_PHOTOS.map((src, i) => (
            <button
              key={src}
              onClick={() => setOpenIdx(i)}
              className="group block w-full mb-5 break-inside-avoid rounded-2xl overflow-hidden bg-white border border-[#E5E1D8] hover:border-[#E69F1B] transition focus:outline-none focus:ring-2 focus:ring-[#E69F1B] focus:ring-offset-2"
              data-testid={`gallery-thumb-${i}`}
              aria-label={`Open photo ${i + 1}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={src}
                  alt={`Latino Institute community photo ${i + 1}`}
                  loading="lazy"
                  className="w-full h-auto block transition duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition" />
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white opacity-0 group-hover:opacity-100 transition">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">Photo {String(i + 1).padStart(2, "0")}</span>
                  <span className="chip bg-[#E69F1B] text-[#1A1A1A]">View</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {openIdx !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 sm:p-8"
          onClick={close}
          data-testid="lightbox"
          role="dialog"
          aria-modal="true"
        >
          <button
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={(e) => { e.stopPropagation(); close(); }}
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={22} />
          </button>

          <button
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={prev}
            aria-label="Previous photo"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={26} />
          </button>

          <button
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
            onClick={next}
            aria-label="Next photo"
            data-testid="lightbox-next"
          >
            <ChevronRight size={26} />
          </button>

          <figure
            className="max-w-6xl max-h-[88vh] w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={GALLERY_PHOTOS[openIdx]}
              alt={`Latino Institute community photo ${openIdx + 1}`}
              className="max-h-[80vh] w-auto max-w-full object-contain rounded-xl"
              data-testid="lightbox-image"
            />
            <figcaption className="mt-4 text-xs uppercase tracking-[0.25em] text-white/80" data-testid="lightbox-caption">
              Photo {String(openIdx + 1).padStart(2, "0")} of {String(GALLERY_PHOTOS.length).padStart(2, "0")}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

function ContactCard({ icon, label, value, href, testId }) {
  const Inner = (
    <div className="bg-[#FDFBF7] text-[#1A1A1A] rounded-2xl p-5 flex items-center gap-4 border border-white/10 hover:translate-y-[-2px] transition" data-testid={testId}>
      <div className="w-11 h-11 rounded-xl bg-[#E69F1B] flex items-center justify-center shrink-0">{icon}</div>
      <div className="leading-tight">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8B3A36] font-semibold">{label}</p>
        <p className="font-['Outfit'] text-lg font-semibold">{value}</p>
      </div>
    </div>
  );
  return href ? <a href={href}>{Inner}</a> : Inner;
}

function App() {
  return (
    <div className="App">
      <Header />
      <main>
        <HeroAbout />
        <ReachCallout />
        <Workshops />
        <ConferencePackage />
        <Handbook />
        <Gallery />
        <Contact />
      </main>
    </div>
  );
}

export default App;
